import { useCallback, useContext } from 'react';
import { useAvailabilityContext } from 'webReservations/context/AvailabilityContext';
import type { GuestPricingImpressionActionType } from './guestPricingTypes';
import { GuestPricingBrowseContext } from './GuestPricingBrowseContext';
import { enqueueGuestPricingImpression } from './guestPricingImpressionQueue';
import { selectAvailabilitiesForPricingImpression } from './selectAvailabilitiesForPricingImpression';

export interface GuestPricingImpressionOverrides {
  selectedTimeOverride?: string | null;
  selectedFloorPlanListingIdsOverride?: string[];
  serviceWindowIndexOverride?: number;
}

export const useGuestPricingImpressionRecorder = (restaurantId: string) => {
  const {
    filteredAvailabilities,
    filteredAvailabilitiesGroupedByTime,
    selectedFloorPlanListingIds,
    selectedTime,
  } = useAvailabilityContext();
  const guestPricingBrowse = useContext(GuestPricingBrowseContext);
  const serviceWindowIndex = guestPricingBrowse?.serviceWindowIndex ?? 0;

  const record = useCallback(
    (
      actionType: GuestPricingImpressionActionType,
      overrides?: GuestPricingImpressionOverrides,
    ) => {
      const listingIds =
        overrides?.selectedFloorPlanListingIdsOverride ??
        selectedFloorPlanListingIds;
      const time = overrides?.selectedTimeOverride ?? selectedTime;
      const windowIndex =
        overrides?.serviceWindowIndexOverride ?? serviceWindowIndex;

      const availabilities = selectAvailabilitiesForPricingImpression({
        filteredAvailabilities,
        filteredAvailabilitiesGroupedByTime,
        selectedFloorPlanListingIds: listingIds,
        selectedTime: time,
        serviceWindowIndex: windowIndex,
      });

      enqueueGuestPricingImpression(restaurantId, {
        actionType,
        clientTimestamp: new Date().toISOString(),
        availabilities,
      });
    },
    [
      restaurantId,
      filteredAvailabilities,
      filteredAvailabilitiesGroupedByTime,
      selectedFloorPlanListingIds,
      selectedTime,
      serviceWindowIndex,
    ],
  );

  return { record };
};
